import { defineComponent as _defineComponent } from 'vue'
import {
  ref, shallowRef, watch,
} from 'vue'
import { Button, Message } from 'element-ui'
import { useNetwork } from '@vueuse/core'

import Uploader from './components/uploader.vue'
import UploadList from './components/list.vue'
import { fileSizeFormatter, formatSize, getMd5 } from '@/utils'
import { BizResCode } from '@/types/axios/public-json-structure'
import { baseURL } from '@/utils/request/index'
import { ChunkUploadCodeEnum, ISimpleUploaderFile } from './type'
import { IS_PRODUCTION } from '@/const'
import { SimpleUploaderModule } from '@/store/modules/simple-uploader'


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    accept: { default: () => ['*'] },
    maxSize: { default: 5 * 1024 },
    query: null
  },
  emits: ['upload-success'],
  setup(__props: any, { expose, emit: emits }) {

const props = __props as { accept: string[], maxSize: number, query?: Record<string, any> }



const fileStatusText = {
  success: '上传成功',
  error: 'error',
  uploading: '上传中',
  paused: '暂停中',
  waiting: '等待中'
}
const attrs = {
  accept: props.accept?.join(',') || '*',
}
const { isOnline } = useNetwork()
// 监听网络状态，网络恢复后，重试未完成上传的文件
watch(isOnline, (newVal) => {
  if(newVal) {
    const fileList = SimpleUploaderModule.uploadFileList
    fileList.forEach((file) => {
      if(file.error) {
        file.retry()
      }
    })
  }
})
/**
 * 上传文件夹 的逻辑是：新建文件夹并上传文件
 * 前端 通过上传组件上传 文件夹 会自动有个 relativePath 参数 如：a/b.json
 * 后端 会创建 一个a文件夹并把b.json 放进去
 * 文件夹里面有多少个文件，就会调多少次上传接口
 */
const panelShow = ref(false) //  上传文件面板是否显示
const collapse = ref(false) // 上传文件面板是否折叠
const dropBoxShow = ref(false) //  拖拽上传是否显示
const uploaderRef = shallowRef<InstanceType<typeof Uploader>>()
const uploadBtn = shallowRef<InstanceType<typeof Button>>() // 选择文件btn
const uploadDirBtn = shallowRef<InstanceType<typeof Button>>() // 选择文件夹btn

const CHUNK_SIZE = 5 // 5MB
const isMock = false
// 上传组件配置项
const options = {
  target: (isMock && !IS_PRODUCTION) ? `http://localhost:4455/api/common/file/upload/chunk` : `${baseURL}/api/common/file/upload/chunk`, // 上传文件-目标 URL
  testUrl: (isMock && !IS_PRODUCTION) ? `http://localhost:4455/api/common/file/uploaded` : `${baseURL}/api/common/file/uploaded`, // 用于检测哪些分片是上传过的请求的地址
  method: 'post',
  testMethod: 'get', // 分片上传检测请求方法
  chunkSize: CHUNK_SIZE * 1024 * 1024, //  每个分片的大小
  fileParameterName: 'chunkFile', //  上传文件时文件的参数名，默认 file
  maxChunkRetries: 0, // 错误时重试次数
  testChunks: true, //  是否开启分片已存在于服务器的校验
  allowDuplicateUploads: true, // 允许重复上传
  withCredentials: true, // 携带 Cookies
  // 强制所有的块都小于指定尺寸
  forceChunkSize: true,
  // 进度信息的回调
  progressCallbacksInterval: 50,
  // 上传文件的并发数量
  simultaneousUploads: 3, //  并发上传数，默认 3
  generateUniqueIdentifier(file: ISimpleUploaderFile) { // 自定义文件标识生成规则
    var relativePath = file.relativePath || file.webkitRelativePath || file.fileName || file.name
    const originFileUid = `${file.lastModified}-${file.size}-${relativePath.replace(/[^0-9a-zA-Z_-]/img, '')}`
    const fileUid = getMd5(originFileUid)
    return fileUid
  },
  checkChunkUploadedByResponse (chunk: any, checkResult: any) { // 用于检测是否需要上传当前分片
    // 根据检查返回结果的code判断当前文件状态 00000 表示已经上传完成， 20000 表示还未上传过，10000 表示部分上传
    const resJson = JSON.parse(checkResult)
    try {
      const uploadIndexArr = resJson.data
      switch(resJson.code) {
        case ChunkUploadCodeEnum.FINISH: {
          return true
        }
        case ChunkUploadCodeEnum.PART_UPLOADING: {
          const isLoaded = uploadIndexArr.indexOf(chunk.offset + 1) >= 0
          return isLoaded
        }
        default: {
          return false
        }
      }
    } catch (error) {
      console.log('error: ', error)
      return false
    }
  },
  /**
   * 处理上传和文件是否已经上传的请求参数
   * @param query
   * @param file
   * @param chunk
   * @param isTest 是否是检测分片上传结果的接口参数
   */
  processParams(query: any, file: ISimpleUploaderFile, chunk: any, isTest: boolean) {
    return isTest ? {
      fileUid: file.uniqueIdentifier,
    } : query
  },
  processResponse(msg: string, cb: (err: any, res: any) => void) {// 请求响应处理
    try {
      const resJson = JSON.parse(msg)
      if(resJson.code === BizResCode.SUCCESS) {
        cb(null, msg)
      } else {
        cb(msg, resJson.message)
      }
    } catch (error) {
      cb('上传出错了', '')
      console.log('error: ', error)
    }
  },
  // headers: {
  //   token: getToken()
  // },
  query: () => ({
    appType: 'sub'
  })
}

// 粘贴图片的信息
const pasteImg = {
  src: '',
  name: ''
}
/**
 * 添加文件的回调函数
 */
const handleFileAdded = (file: ISimpleUploaderFile) => {
  const fileNameArr = file.name.split('.')
  const fileType = `.${fileNameArr[fileNameArr.length - 1]}`
  const isAccept = props.accept.includes(fileType)
  let message = ''
  const maxSize = props.maxSize * 1024 * 1024
  if(!isAccept) {
    message = `只支持${props.accept.join('、')}格式文件`
  } else if (file.size > maxSize) {
    // 选择的文件超出限制大小
    const formatterSize = fileSizeFormatter(maxSize, 0)
    message = `文件大小不能超过${formatterSize}，请重新选择`
  }
  if(message) {
    Message.error(message)
    dropBoxShow.value = false
    // https://github.com/simple-uploader/vue-uploader/blob/master/README_zh-CN.md#%E4%BA%8B%E4%BB%B6
    file.ignored = true //  本次选择的文件过滤掉
    return
  }
  dropBoxShow.value = false
  panelShow.value = true
  collapse.value = false
  // hack 如果立刻触发上传会导致template 里自定义状态 file.statusStr 为''
  setTimeout(() => {
    file.resume()
  }, 300)
}
/**
 * 关闭上传面板，并停止上传
 */
const handleClosePanel = () => {
  // uploader.value.uploader.cancel()
  panelShow.value = false
}
const uploadListRef = shallowRef<InstanceType<typeof UploadList>>()

/**
 * 文件上传成功 回调函数
 * @param {object} rootFile 成功上传的文件所属的根 Uploader.File 对象，它应该包含或者等于成功上传文件
 * @param {object} file 当前成功的 Uploader.File 对象本身
 * @param {string} response 服务端响应内容，永远都是字符串
 */
const handleFileSuccess = (rootFile: ISimpleUploaderFile, file: ISimpleUploaderFile, response: any) => {
  console.log('handleFileSuccess: ', { file, response })
  // 进度和速度的更新不能响应式，手动刷新一下上传文件的进度和速度
  uploadListRef.value?.$forceUpdate()
  if (response === '') {
    file.statusStr = '上传失败'
    file.error = true
    return
  }
  file.statusStr = '上传成功'
  // 上传成功，回传当前文件的标识
  emits('upload-success', file.uniqueIdentifier, (result: string) => {
    file.statusStr = result || '任务提交成功'
    uploadListRef.value?.$forceUpdate()
  })
}
/**
 * 文件上传失败 回调函数
 * @param {object} rootFile 成功上传的文件所属的根 Uploader.File 对象，它应该包含或者等于成功上传文件
 * @param {object} file 当前成功的 Uploader.File 对象本身
 * @param {string} response 服务端响应内容，永远都是字符串
 */
const handleFileError = (rootFile: ISimpleUploaderFile, file: ISimpleUploaderFile, response: any) => {
  console.log('handleFileError: ', { file, response })
  // 进度和速度的更新不能响应式，手动刷新一下上传文件的进度和速度
  uploadListRef.value?.$forceUpdate()
  Message.error(response || '文件上传失败，请重试')
}
const handlePaste = () => {
  console.log('handlePaste')
}
// 向外暴露上传文件事件
const handleUploadBtnClick = () => {
  (uploadBtn.value?.$el as HTMLButtonElement).click()
}
// 向外暴露上传文件夹事件
const handleUploadDirBtnClick = () => {
  (uploadDirBtn.value?.$el as HTMLButtonElement).click()
}
function onFileProgress (rootFile: ISimpleUploaderFile, file: ISimpleUploaderFile) {
  file.percentage = Number((file.progress() * 100).toFixed(2))
  // 进度和速度的更新不能响应式，手动刷新一下上传文件的进度和速度
  uploadListRef.value?.$forceUpdate()
}
function getSpeed(file: ISimpleUploaderFile) {
  return `${formatSize(file.averageSpeed)} / s`
}
function handleFileChange(fileList: ISimpleUploaderFile[]) {
  SimpleUploaderModule.SetUploadFileList(fileList)
}
function getCompletedList (list: any[]) {
  const result = list.filter(item => item.completed)
    .reverse()
  return result
}
function getUnCompletedList (list: any[]) {
  const result = list.filter(item => !item.completed)
  return result
}
expose({
  panelShow,
  dropBoxShow,
  handleUploadBtnClick,
  handleUploadDirBtnClick
})

return { __sfc: true,props, fileStatusText, attrs, isOnline, panelShow, collapse, dropBoxShow, uploaderRef, uploadBtn, uploadDirBtn, CHUNK_SIZE, isMock, options, pasteImg, handleFileAdded, handleClosePanel, uploadListRef, emits, handleFileSuccess, handleFileError, handlePaste, handleUploadBtnClick, handleUploadDirBtnClick, onFileProgress, getSpeed, handleFileChange, getCompletedList, getUnCompletedList, Uploader }
}

})